import { env } from "env";
import { useCallback } from "react";
import { getLocalStorageAccessToken } from "utils/helpers/localStorage";
import {
  joinBaseUrlAndPath,
  normalizeBareNumericEnvPath,
  replaceParams,
  replaceTrailingNumericWithEnvLang,
  usesDecodeParamsEnvSegment,
} from "pages/grid/utils/gridHelpers";

type TableRecord = Record<string, any>;

export function useGridDeleteRow({
  envLang,
  isDashboardLayout,
  isEnvironmentTable,
  isAdminUsersTable,
  isVersionsTable, 
  isTvaTable,
  isAnnexTable,
  isParameterTable,
  isDiscountManagementTable,
  isFaqTable,
  isTutorialsLayout,
  isAdminTutorialsLayout,
  refetchDatasource,
  refetchTutorials,
  refetchEnvironments,
  refetchVersions,
  refetchAdminUsers,
  refetchMenu,
  refetchFaq,
  selectedDataSource,
  trashDatasourceRequest,
  setDeletingRow,
}: {
  envLang: { lang: string; envNumeric: number | undefined };
  isDashboardLayout: boolean;
  isEnvironmentTable: boolean;
  isAdminUsersTable: boolean;
  isVersionsTable: boolean;
  isTvaTable: boolean;
  isAnnexTable: boolean;
  isParameterTable: boolean;
  isDiscountManagementTable: boolean;
  isFaqTable: boolean;
  isTutorialsLayout: boolean;
  isAdminTutorialsLayout: boolean;
  refetchDatasource: () => Promise<any>;
  refetchTutorials?: () => Promise<any>;
  refetchEnvironments?: () => Promise<any>;
  refetchAdminUsers?: () => Promise<any>;
  refetchVersions?: () => Promise<any>;
  refetchMenu?: () => Promise<any>;
  refetchFaq?: () => Promise<any>;
  selectedDataSource: any;
  trashDatasourceRequest: { url: string; method?: string } | null;
  setDeletingRow: (row: TableRecord | null) => void;
}) {
  const deleteRow = useCallback(async (row: TableRecord) => {
    if (!trashDatasourceRequest || !selectedDataSource) return;

    const rowId = row.id ?? row.key;
    if (rowId == null) return;

    const { lang, envNumeric } = envLang;
    const rawEndpoint = trashDatasourceRequest.url;
    const baseUrl = selectedDataSource.baseUrl || "";
    const useComposite =
      usesDecodeParamsEnvSegment(rawEndpoint) ||
      usesDecodeParamsEnvSegment(baseUrl) ||
      /##env##/.test(rawEndpoint);
    const envComposite = envNumeric == null ? "" : `env=${envNumeric}&lang=${lang}`;
    const params: Record<string, string | number> = {
      id: rowId,
      lang,
      ...(envNumeric != null && !Number.isNaN(envNumeric)
        ? { env: useComposite ? envComposite : String(envNumeric) }
        : {}),
    };

    let endpoint = replaceParams(rawEndpoint, params);
    const isAccessoryDeleteEndpoint = /(^|\/)accessorymgt(\/|$)|(^|\/)accessories?(\/|$)|(^|\/)accessorygroup(\/|$)/i.test(
      `${baseUrl}/${rawEndpoint}`
    );
    const hasIdPlaceholder = /##id##|\{id\}|:id\b/i.test(rawEndpoint);
    if (isTvaTable || isAnnexTable || isParameterTable || isDiscountManagementTable) {
      const normalized = endpoint.replace(/\/+$/, "");
      const vatBase = normalized.replace(/\/[^/]+$/, "");
      endpoint = `${vatBase}/${rowId}`;
    } else {
      if (envNumeric != null && !Number.isNaN(envNumeric)) {
        endpoint = replaceTrailingNumericWithEnvLang(baseUrl, endpoint, envNumeric, lang);
        endpoint = normalizeBareNumericEnvPath(endpoint, envNumeric, lang);
      }
      endpoint = endpoint.replace(/\/+$/, "");

      if (isAccessoryDeleteEndpoint && !hasIdPlaceholder) {
        // Accessory delete expects id in the path; env/lang segment must not replace id.
        endpoint = endpoint.replace(/\/?env=[^/]+&lang=[^/]+$/i, "").replace(/\/+$/, "");
        endpoint = `${endpoint}/${rowId}`;
      }
    }

    try {
      const token = getLocalStorageAccessToken();
      const authHeaders = token ? { authorization: `Bearer ${token}` } : undefined;
      let finalPath = joinBaseUrlAndPath(baseUrl, endpoint).replace(/^\/+/, "");
      if (isAccessoryDeleteEndpoint) {
        finalPath = finalPath
          .replace(/\/?env=[^/]+&lang=[^/]+(?=\/|$)/gi, "")
          .replace(/\/+$/, "");
        if (!new RegExp(String.raw`/${String(rowId)}(?:$|\?)`).test(finalPath)) {
          finalPath = `${finalPath}/${rowId}`;
        }
      }
      const finalUrl = `${env.api_url}/${finalPath}`;
      const isAccessoryGroupEndpoint = /(^|\/)accessorygroup(\/|$)/i.test(`${baseUrl}/${rawEndpoint}`);
      const payload: Record<string, unknown> = { id: rowId };
      if ((!isAccessoryDeleteEndpoint || isAccessoryGroupEndpoint) && envNumeric != null && !Number.isNaN(envNumeric)) {
        payload.env = String(envNumeric);
        payload.lang = lang;
      }

      const response = await fetch(finalUrl, {
        method: String(trashDatasourceRequest.method || "DELETE").toUpperCase(),
        headers: authHeaders
          ? { "Content-Type": "application/json", ...authHeaders }
          : { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Delete failed: ${response.status}`);
      }

      setDeletingRow(null);

      const callRefetchSafely = async (fn?: () => Promise<any>) => {
        if (!fn) return;
        try {
          await fn();
        } catch (refetchError) {
          const message = String((refetchError as Error)?.message ?? refetchError ?? "");
          if (/has not been started yet/i.test(message)) {
            return;
          }
          throw refetchError;
        }
      };

      const refetchTasks: Array<Promise<unknown>> = [];
      if (isFaqTable && refetchFaq) {
        refetchTasks.push(callRefetchSafely(refetchFaq));
      }
      if (isDashboardLayout && refetchMenu) {
        refetchTasks.push(callRefetchSafely(refetchMenu));
      } else if (isTutorialsLayout || isAdminTutorialsLayout) {
        refetchTasks.push(callRefetchSafely(refetchTutorials));
      } else if (isEnvironmentTable) {
        refetchTasks.push(callRefetchSafely(refetchEnvironments));
      } else if (isAdminUsersTable) {
        refetchTasks.push(callRefetchSafely(refetchAdminUsers));
      } else if (isVersionsTable) {
        refetchTasks.push(callRefetchSafely(refetchVersions));
      } else {
        refetchTasks.push(callRefetchSafely(refetchDatasource));
      }
      await Promise.allSettled(refetchTasks);
    } catch (error) {
      console.error("Delete row error", error);
    }
  }, [envLang, isDashboardLayout, isEnvironmentTable, isAdminUsersTable, isTvaTable, isAnnexTable, isParameterTable, isDiscountManagementTable, isFaqTable, isTutorialsLayout, isAdminTutorialsLayout, refetchDatasource, refetchTutorials, refetchEnvironments, refetchAdminUsers, refetchMenu, refetchFaq, selectedDataSource, setDeletingRow, trashDatasourceRequest]);

  const requestDeleteRow = useCallback((row: TableRecord) => {
    const rowId = row.id ?? row.key;
    if (rowId == null) return;
    setDeletingRow({
      ...row,
      id: row.id ?? rowId,
      key: row.key ?? rowId,
    });
  }, [setDeletingRow]);

  return { deleteRow, requestDeleteRow };
}
